import React, { useState, useEffect } from 'react';
import { Menu, X, Crown, Mail, Lock, LogIn, Eye, EyeOff, ShoppingBag } from 'lucide-react';
import { useCart } from '../context/CartContext';

const Header = ({ currentPage, navigateToPage }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [loginData, setLoginData] = useState({
    email: '',
    password: ''
  });
  
  // Cart functionality
  const { totalItems, toggleCart } = useCart();
  const isHomePage = currentPage === 'home';

  useEffect(() => {
    const handleScroll = () => {
      const isScrolled = window.scrollY > 10;
      setScrolled(isScrolled);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const toggleMenu = () => setIsMenuOpen(!isMenuOpen);

  const scrollToSection = (sectionId) => {
    if (currentPage !== 'home') {
      navigateToPage('home');
      setTimeout(() => {
        const element = document.getElementById(sectionId);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    } else {
      const element = document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
    setIsMenuOpen(false);
  };

  const handleLoginClick = () => {
    setShowLoginModal(true);
    setIsMenuOpen(false);
  };

  const handleCloseModal = () => {
    setShowLoginModal(false);
    setLoginData({ email: '', password: '' });
    setShowPassword(false);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setLoginData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleLogin = (e) => {
    e.preventDefault();
    if (loginData.email && loginData.password) {
      // Redirecționare către dashboard
      window.location.href = 'https://dashboard.trusoubotez.com/';
    } else {
      alert('Te rog să completezi toate câmpurile!');
    }
  };

  const handleNavigation = (page) => {
    navigateToPage(page);
    setIsMenuOpen(false);
  };

  return (
    <>
      <header className={`sticky top-0 z-50 transition-all duration-500 ${
        scrolled 
          ? 'glass-navy backdrop-blur-xl shadow-2xl' 
          : 'bg-white/95 backdrop-blur-sm shadow-lg'
      }`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
          {/* Logo Animat */}
          <div className="flex-shrink-0 group">
          <div className="flex items-center space-x-3">
          <div className="relative">
          <Crown className="w-6 h-6 text-gold-600 float absolute -top-1 -right-1" size={16} />
          <h1 
          className="text-2xl font-bold gradient-text cursor-pointer hover:scale-105 transition-all duration-300 font-display relative"
          onClick={() => navigateToPage('home')}
          >
          BOTEZ DE POVESTE
          <div className="absolute -bottom-1 left-0 w-0 h-0.5 bg-gradient-to-r from-navy-600 to-gold-500 group-hover:w-full transition-all duration-500"></div>
          </h1>
          </div>
          </div>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden md:block">
          <ul className="flex space-x-8">
          {/* Home page navigation items */}
          {isHomePage && [
            { name: 'Acasă', id: 'acasa' },
              { name: 'Produse', id: 'produse' },
            { name: 'Servicii', id: 'servicii' },
            { name: 'Despre Noi', id: 'despre' },
          { name: 'Contact', id: 'contact' }
          ].map((item, index) => (
            <li key={item.id} className={`fade-in-up stagger-${index + 1}`}>
              <button 
                  onClick={() => scrollToSection(item.id)}
                    className="relative text-gray-700 hover:text-navy-600 transition-all duration-300 font-medium py-2 px-1 group overflow-hidden"
                  >
                    <span className="relative z-10">{item.name}</span>
                    <div className="absolute bottom-0 left-0 w-0 h-0.5 bg-gradient-to-r from-navy-600 to-gold-500 group-hover:w-full transition-all duration-300"></div>
                  <div className="absolute inset-0 bg-gradient-to-r from-cream-50 to-gold-50 scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left rounded-lg"></div>
              </button>
          </li>
          ))}
          
          {/* Always show Despre proiect */}
          <li className="fade-in-up">
            <button
              onClick={() => handleNavigation('despre-proiect')}
              className={`relative transition-all duration-300 font-medium py-2 px-1 group overflow-hidden ${
                currentPage === 'despre-proiect' 
                  ? 'text-navy-600 font-bold' 
                  : 'text-gray-700 hover:text-navy-600'
              }`}
            >
              <span className="relative z-10">Despre proiect</span>
              <div className={`absolute bottom-0 left-0 h-0.5 bg-gradient-to-r from-navy-600 to-gold-500 transition-all duration-300 ${
                currentPage === 'despre-proiect' ? 'w-full' : 'w-0 group-hover:w-full'
              }`}></div>
              <div className="absolute inset-0 bg-gradient-to-r from-cream-50 to-gold-50 scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left rounded-lg"></div>
            </button>
          </li>
          
          {/* If on project page, show Home button */}
          {!isHomePage && (
            <li className="fade-in-up">
              <button 
                onClick={() => handleNavigation('home')}
                className="relative text-gray-700 hover:text-navy-600 transition-all duration-300 font-medium py-2 px-1 group overflow-hidden"
              >
                <span className="relative z-10">Acasă</span>
                <div className="absolute bottom-0 left-0 w-0 h-0.5 bg-gradient-to-r from-navy-600 to-gold-500 group-hover:w-full transition-all duration-300"></div>
                <div className="absolute inset-0 bg-gradient-to-r from-cream-50 to-gold-50 scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left rounded-lg"></div>
              </button>
            </li>
          )}
          </ul>
          </nav>
          
          {/* Cart Button and Login Clienti Button - Desktop */}
          <div className="hidden md:flex items-center space-x-4">
            {/* Shopping Cart Button */}
            <button 
              onClick={toggleCart}
              className="relative p-2.5 text-gray-700 hover:text-navy-600 hover:bg-cream-50 rounded-full transition-all duration-300 group"
            >
              <ShoppingBag className="w-6 h-6 group-hover:scale-110 transition-transform duration-300" />
              {totalItems > 0 && (
                <span className="absolute -top-1 -right-1 w-5 h-5 bg-gradient-to-r from-navy-600 to-navy-700 text-white text-xs font-bold rounded-full flex items-center justify-center animate-pulse">
                  {totalItems > 9 ? '9+' : totalItems}
                </span>
              )}
            </button>
            
            {/* Login Button */}
            <button 
              onClick={handleLoginClick}
              className="login-client-btn relative px-6 py-2.5 bg-gradient-to-r from-navy-600 via-navy-700 to-navy-800 text-white rounded-full font-semibold shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300 group overflow-hidden border border-gold-400/20"
            >
              <div className="absolute inset-0 bg-gradient-to-r from-gold-500/20 to-gold-600/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              <div className="relative z-10 flex items-center space-x-2">
                <Crown className="w-4 h-4 text-gold-400 group-hover:rotate-12 transition-transform duration-300" />
                <span className="gradient-text-white">Login Clienti</span>
              </div>
              <div className="absolute -top-1 -right-1 w-3 h-3 bg-gold-400 rounded-full animate-pulse opacity-80"></div>
            </button>
          </div>

            {/* Mobile menu button and cart */}
            <div className="md:hidden flex items-center space-x-2">
              {/* Mobile Cart Button */}
              <button 
                onClick={toggleCart}
                className="relative p-2.5 text-gray-700 hover:text-navy-600 hover:bg-cream-50 rounded-full transition-all duration-300 group"
              >
                <ShoppingBag className="w-5 h-5 group-hover:scale-110 transition-transform duration-300" />
                {totalItems > 0 && (
                  <span className="absolute -top-1 -right-1 w-4 h-4 bg-gradient-to-r from-navy-600 to-navy-700 text-white text-xs font-bold rounded-full flex items-center justify-center animate-pulse">
                    {totalItems > 9 ? '9+' : totalItems}
                  </span>
                )}
              </button>
              
              {/* Mobile Menu Button */}
              <button 
                onClick={toggleMenu} 
                className="morph-button relative text-gray-700 hover:text-navy-600 focus:outline-none focus:ring-2 focus:ring-navy-500 p-3 rounded-full hover:bg-cream-50 transition-all duration-300"
              >
                <div className="relative z-10">
                  {isMenuOpen ? (
                    <X size={24} className="transform rotate-0 transition-transform duration-300" />
                  ) : (
                    <Menu size={24} className="transform rotate-0 transition-transform duration-300" />
                  )}
                </div>
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Navigation */}
        <div className={`md:hidden overflow-hidden transition-all duration-500 ${
          isMenuOpen 
            ? 'max-h-96 opacity-100' 
            : 'max-h-0 opacity-0'
        }`}>
          <div className="glass-cream border-t border-gold-200/30 backdrop-blur-xl">
            <div className="px-4 py-3 space-y-2">
              {/* Mobile Login Clienti Button */}
              <button 
                onClick={handleLoginClick}
                className="login-client-btn-mobile w-full px-4 py-3 bg-gradient-to-r from-navy-600 via-navy-700 to-navy-800 text-white rounded-xl font-semibold shadow-lg hover:shadow-xl transform hover:scale-[1.02] transition-all duration-300 group overflow-hidden border border-gold-400/20 mb-3"
              >
                <div className="absolute inset-0 bg-gradient-to-r from-gold-500/20 to-gold-600/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                <div className="relative z-10 flex items-center justify-center space-x-2">
                  <Crown className="w-4 h-4 text-gold-400 group-hover:rotate-12 transition-transform duration-300" />
                  <span className="gradient-text-white">Login Clienti</span>
                </div>
              </button>
              
              {/* Home page navigation items */}
              {isHomePage && [
                { name: 'Acasă', id: 'acasa' },
                { name: 'Produse', id: 'produse' },
                { name: 'Servicii', id: 'servicii' },
                { name: 'Despre Noi', id: 'despre' },
                { name: 'Contact', id: 'contact' }
              ].map((item, index) => (
                <button 
                  key={item.id}
                  onClick={() => scrollToSection(item.id)}
                  className={`card-hover block w-full text-left px-4 py-3 text-gray-700 hover:text-navy-600 hover:bg-gradient-to-r hover:from-cream-50 hover:to-gold-50 rounded-xl font-medium transition-all duration-300 transform translate-x-0 ${
                    isMenuOpen ? 'fade-in-right' : ''
                  }`}
                  style={{ animationDelay: `${index * 0.1}s` }}
                >
                  {item.name}
                </button>
              ))}
              
              {/* Always show Despre proiect */}
              <button 
                onClick={() => handleNavigation('despre-proiect')}
                className={`card-hover block w-full text-left px-4 py-3 font-medium transition-all duration-300 transform translate-x-0 rounded-xl ${
                  currentPage === 'despre-proiect' 
                    ? 'text-navy-600 bg-gradient-to-r from-navy-50 to-gold-50 font-bold' 
                    : 'text-gray-700 hover:text-navy-600 hover:bg-gradient-to-r hover:from-cream-50 hover:to-gold-50'
                } ${isMenuOpen ? 'fade-in-right' : ''}`}
              >
                Despre proiect
              </button>
              
              {/* If on project page, show Home button */}
              {!isHomePage && (
                <button 
                  onClick={() => handleNavigation('home')}
                  className={`card-hover block w-full text-left px-4 py-3 text-gray-700 hover:text-navy-600 hover:bg-gradient-to-r hover:from-cream-50 hover:to-gold-50 rounded-xl font-medium transition-all duration-300 transform translate-x-0 ${
                    isMenuOpen ? 'fade-in-right' : ''
                  }`}
                >
                  Acasă
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Floating particles pentru header */}
        <div className="absolute top-0 left-0 w-full h-full pointer-events-none overflow-hidden">
          <div className="particle-1 absolute top-2 left-10 w-1 h-1 bg-gold-400 rounded-full opacity-40"></div>
          <div className="particle-2 absolute top-6 right-20 w-1.5 h-1.5 bg-navy-400 rounded-full opacity-30"></div>
          <div className="particle-1 absolute top-3 left-1/3 w-0.5 h-0.5 bg-emerald-500 rounded-full opacity-60"></div>
        </div>
      </header>

      {/* Modal de Login */}
      {showLoginModal && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
          {/* Overlay cu blur */}
          <div 
            className="absolute inset-0 bg-black/50 backdrop-blur-sm transition-opacity duration-300"
            onClick={handleCloseModal}
          ></div>
          
          {/* Modal Content */}
          <div className="relative bg-white/95 backdrop-blur-xl rounded-3xl shadow-2xl p-8 w-full max-w-md mx-auto border border-gold-200/30 transform transition-all duration-500 scale-100">
            {/* Close Button */}
            <button
              onClick={handleCloseModal}
              className="absolute top-4 right-4 p-2 text-gray-400 hover:text-gray-600 transition-colors duration-200 hover:bg-gray-100/50 rounded-full"
            >
              <X size={24} />
            </button>

            {/* Header Modal */}
            <div className="text-center mb-8">
              <div className="flex items-center justify-center mb-4">
                <div className="relative">
                  <Crown className="w-8 h-8 text-gold-600" />
                  <div className="absolute -top-1 -right-1 w-3 h-3 bg-gold-400 rounded-full animate-pulse"></div>
                </div>
              </div>
              <h2 className="text-2xl font-bold gradient-text mb-2">Login Clienti</h2>
              <p className="text-gray-600 text-sm">Accesează dashboard-ul tău exclusiv</p>
            </div>

            {/* Form de Login */}
            <form onSubmit={handleLogin} className="space-y-6">
              {/* Campo de Email */}
              <div className="relative">
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                  Adresa de Email
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={loginData.email}
                    onChange={handleInputChange}
                    className="w-full pl-12 pr-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-navy-500 focus:border-transparent transition-all duration-300 bg-white/80 backdrop-blur-sm text-gray-700 placeholder-gray-400"
                    placeholder="ex: nume@email.com"
                    required
                  />
                </div>
              </div>

              {/* Campo de Parolă */}
              <div className="relative">
                <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                  Parolă
                </label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <input
                    type={showPassword ? "text" : "password"}
                    id="password"
                    name="password"
                    value={loginData.password}
                    onChange={handleInputChange}
                    className="w-full pl-12 pr-12 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-navy-500 focus:border-transparent transition-all duration-300 bg-white/80 backdrop-blur-sm text-gray-700 placeholder-gray-400"
                    placeholder="Introduceți parola"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors duration-200"
                  >
                    {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                  </button>
                </div>
              </div>

              {/* Buton de Login */}
              <button
                type="submit"
                className="w-full bg-gradient-to-r from-navy-600 via-navy-700 to-navy-800 text-white py-3 px-6 rounded-xl font-semibold shadow-lg hover:shadow-xl transform hover:scale-[1.02] transition-all duration-300 group overflow-hidden border border-gold-400/20"
              >
                <div className="absolute inset-0 bg-gradient-to-r from-gold-500/20 to-gold-600/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                <div className="relative z-10 flex items-center justify-center space-x-2">
                  <LogIn className="w-5 h-5 text-gold-400 group-hover:translate-x-1 transition-transform duration-300" />
                  <span>Accesează Dashboard-ul</span>
                </div>
              </button>
            </form>

            {/* Footer Modal */}
            <div className="mt-6 text-center">
              <p className="text-xs text-gray-500">
                Prin conectare accepți 
                <span className="text-navy-600 hover:text-navy-800 cursor-pointer"> termenii și condițiile</span>
              </p>
            </div>

            {/* Decorative elements */}
            <div className="absolute top-0 left-0 w-full h-full pointer-events-none overflow-hidden rounded-3xl">
              <div className="absolute top-4 left-4 w-2 h-2 bg-gold-400/30 rounded-full animate-pulse"></div>
              <div className="absolute bottom-4 right-4 w-1.5 h-1.5 bg-navy-400/40 rounded-full animate-pulse" style={{animationDelay: '1s'}}></div>
              <div className="absolute top-1/2 right-6 w-1 h-1 bg-emerald-500/50 rounded-full animate-pulse" style={{animationDelay: '2s'}}></div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Header;
